import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';

interface SpecialOrder {
  id: string;
  orderNumber: string;
  status: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  companyName: string;
  totalAmount: number;
  subtotal: number;
  shippingCost: number;
  markupPercent: number;
  specialOrderType: string;
  internalNotes: string;
  createdAt: string;
  shippingStreet: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  items?: any[];
}

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_production', label: 'In Production' },
  { value: 'ready_to_ship', label: 'Ready to Ship' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'completed', label: 'Completed' },
];

export default function SpecialOrdersPage() {
  const [orders, setOrders] = useState<SpecialOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const response = await fetch(`/api/orders/special-list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load special orders');
      const data = await response.json();
      setOrders(data || []);
    } catch (error) {
      console.error('Failed to load special orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      setActionLoading(orderId);
      await api.orders.updateOrderStatus(orderId, newStatus as any);
      await loadOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredOrders = orders.filter(order =>
    (order.orderNumber || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (order.customerName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (order.companyName || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount: number) =>
    `$${(Number(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div>
      <PageHeader
        title="Special Orders"
        subtitle="Custom BOM orders created from the BOM calculator"
      />

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by order #, customer name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipping</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <p className="text-lg font-medium">No special orders found</p>
                      <p className="text-sm mt-1">Create special orders from the BOM Calculator page</p>
                    </td>
                  </tr>
                )}
                {filteredOrders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Link to={`/orders/${order.id}`} className="text-purple-600 hover:text-purple-800 font-medium">
                        {order.orderNumber}
                      </Link>
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700">
                        SPECIAL
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-gray-900">{order.customerName || order.companyName}</div>
                      {order.customerEmail && (
                        <div className="text-gray-500 text-xs">{order.customerEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {order.specialOrderType || 'assembly'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge status={order.status} type="order" />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(order.subtotal)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(order.shippingCost)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(order.totalAmount)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(order.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Link to={`/orders/${order.id}`} className="text-gray-600 hover:text-gray-900">
                          View
                        </Link>
                        {(order.status === 'submitted' || order.status === 'pending_payment') && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'in_production')}
                            disabled={actionLoading === order.id}
                            className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50"
                            title="Build the housing cut sheet and route to SAW / Box-Assembly on the floor"
                          >
                            {actionLoading === order.id ? '...' : 'Send to Production'}
                          </button>
                        )}
                        {order.status === 'in_production' && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'ready_to_ship')}
                            disabled={actionLoading === order.id}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            title="Mark Ready to Ship — appears in shipping portal queue for label printing"
                          >
                            {actionLoading === order.id ? '...' : 'Ready to Ship'}
                          </button>
                        )}
                        {order.status === 'ready_to_ship' && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'shipped')}
                            disabled={actionLoading === order.id}
                            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {actionLoading === order.id ? '...' : 'Mark Shipped'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
